import React from 'react';
import clsx from 'clsx';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import IconReload from '@/assets/icons/icon-reload.svg?react';
import IconAlert from '@/assets/icons/icon-alert.svg?react';

interface ErrorFallbackProps {
  className?: string;
  inspector?: string;
  title?: string;
  message?: string;
  onRefresh?: () => void;
}

/**
 * Full-screen error fallback for application root errors
 */
export const FullScreenErrorFallback: React.FC<ErrorFallbackProps> = ({
  className = '',
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please refresh the page to try again.',
  onRefresh = () => window.location.reload(),
}) => {
  return (
    <div
      className={clsx(
        'relative flex h-[100vh] w-full flex-col items-center overflow-hidden bg-neutral-800 px-4',
        className,
      )}
    >
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center gap-10">
        <div className="flex flex-col items-center gap-4.5">
          <div className="flex flex-col items-center gap-4">
            <div className="flex w-full items-center justify-center gap-2">
              <IconAlert className="fill-coral-500 h-8 w-8" aria-hidden />
              <h1 className="text-neutral-0 flex h-8 items-center font-sans text-3xl leading-normal font-semibold">
                {title}
              </h1>
            </div>
          </div>
          <p className="text-center font-sans text-lg leading-normal font-medium text-neutral-400">
            {message}
            <br />
            If the problem persists, visit{' '}
            <a
              href="http://support.leadpages.com"
              className="text-neutral-0 font-sans text-lg leading-normal font-medium"
            >
              support.leadpages.com
            </a>
          </p>
        </div>
        <PrimaryButton
          text="Refresh Page"
          icon={IconReload}
          size="md"
          state="default"
          onClick={onRefresh}
        />
      </div>
      <div className="mt-12 mb-6 w-full max-w-md text-center text-xs leading-none font-medium text-neutral-400">
        ©2010-2025 Leadpages (US), Inc., All Rights Reserved.
      </div>
    </div>
  );
};

/**
 * Canvas error fallback for CraftRenderer errors
 */
export const CanvasErrorFallback: React.FC<ErrorFallbackProps> = ({
  className = '',
  title = 'Page rendering error',
  message = 'There was a problem rendering your page.',
  onRefresh = () => window.location.reload(),
}) => {
  return (
    <div
      className={clsx(
        'flex h-full w-full flex-col items-center justify-center gap-6 bg-neutral-100 p-8',
        className,
      )}
    >
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-2">
          <IconAlert className="fill-coral-500 h-6 w-6" aria-hidden />
          <h2 className="font-sans text-xl font-semibold text-neutral-800">{title}</h2>
        </div>
        <p className="text-center font-sans text-sm font-medium text-neutral-500">
          {message}
          <br />
          Please refresh the page to try again.
        </p>
      </div>
      <PrimaryButton
        text="Refresh Page"
        icon={IconReload}
        size="sm"
        state="default"
        onClick={onRefresh}
      />
    </div>
  );
};

/**
 * Panel error fallback for sidebar panels
 */
export const PanelErrorFallback: React.FC<ErrorFallbackProps> = ({
  className = '',
  title = 'Error loading panel',
  message = 'Please refresh the page to try again.',
  onRefresh = () => window.location.reload(),
}) => {
  return (
    <div
      className={clsx(
        'flex h-full w-full flex-col items-center justify-center gap-4 p-4',
        className,
      )}
    >
      <div className="flex flex-col items-center gap-2">
        <IconAlert className="fill-coral-500 h-5 w-5" aria-hidden />
        <p className="text-center font-sans text-sm font-medium text-neutral-600">{title}</p>
        <p className="text-center font-sans text-xs text-neutral-500">{message}</p>
      </div>
      <button
        onClick={onRefresh}
        className="flex items-center gap-1.5 rounded bg-neutral-200 px-3 py-1.5 font-sans text-xs font-medium text-neutral-700 hover:bg-neutral-300"
      >
        <IconReload className="h-3 w-3" />
        Refresh
      </button>
    </div>
  );
};

/**
 * Inspector error fallback for Inspector panels
 * Styled to look like an actual inspector panel
 */
export const InspectorErrorFallback: React.FC<ErrorFallbackProps> = ({
  className = '',
  inspector = 'Inspector',
  title = 'Error loading inspector',
  message = 'Please refresh the page to try again.',
  onRefresh = () => window.location.reload(),
}) => {
  return (
    <div
      className={clsx(
        'bg-neutral-0 flex h-[calc(100vh-52px)] w-[240px] flex-col shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)]',
        className,
      )}
    >
      {/* Header - matches InspectorHeader style */}
      <div className="bg-neutral-0 inline-flex h-13 w-full items-center justify-between self-stretch border-b border-neutral-100 px-3">
        <div className="flex items-center justify-start gap-1">
          <p className="text-heading-ms justify-start font-semibold text-neutral-900">
            {inspector}
          </p>
        </div>
      </div>

      {/* Content - centered error message */}
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <div className="flex flex-col items-center gap-2">
          <IconAlert className="fill-coral-500 h-5 w-5" aria-hidden />
          <p className="text-center font-sans text-sm font-medium text-neutral-600">{title}</p>
          <p className="text-center font-sans text-xs text-neutral-500">{message}</p>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 rounded-md bg-neutral-100 px-3 py-2 font-sans text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
        >
          <IconReload className="h-3.5 w-3.5" />
          Refresh Page
        </button>
      </div>
    </div>
  );
};
